(function () {
  const defaults = {
    colors: ["#9a081f", "#7d7e85", "#d3d0c6", "#c8a1a4"],
    distortion: 0.81,
    swirl: 0.20,
    scale: 1.33,
    rotation: 97,
    speed: 0.44,
    edge: 0.64,
    film: 0.62,
    monochrome: false,
    maxPixelRatio: 1.75,
    maxFps: 30
  };

  const vertexSource = `
    attribute vec2 a_position;
    varying vec2 v_uv;

    void main() {
      v_uv = a_position * 0.5 + 0.5;
      gl_Position = vec4(a_position, 0.0, 1.0);
    }
  `;

  const fragmentSource = `
    precision highp float;

    varying vec2 v_uv;

    uniform vec2 u_resolution;
    uniform float u_time;
    uniform vec3 u_colors[4];
    uniform vec2 u_centers[8];
    uniform float u_distortion;
    uniform float u_swirl;
    uniform float u_scale;
    uniform float u_rotation;
    uniform float u_speed;
    uniform float u_edge;
    uniform float u_film;
    uniform float u_mono;

    #define PI 3.141592653589793

    float hash12(vec2 p) {
      vec3 p3 = fract(vec3(p.xyx) * 0.1031);
      p3 += dot(p3, p3.yzx + 33.33);
      return fract((p3.x + p3.y) * p3.z);
    }

    float noise(vec2 p) {
      vec2 i = floor(p);
      vec2 f = fract(p);
      vec2 u = f * f * (3.0 - 2.0 * f);

      float a = hash12(i);
      float b = hash12(i + vec2(1.0, 0.0));
      float c = hash12(i + vec2(0.0, 1.0));
      float d = hash12(i + vec2(1.0, 1.0));

      return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
    }

    float fbm(vec2 p) {
      float value = 0.0;
      float amplitude = 0.5;
      for (int i = 0; i < 5; i++) {
        value += amplitude * noise(p);
        p = mat2(1.6, -1.2, 1.2, 1.6) * p;
        amplitude *= 0.5;
      }
      return value;
    }

    float softFbm(vec2 p) {
      float value = 0.0;
      value += 0.58 * noise(p);
      p = mat2(1.34, -0.72, 0.72, 1.34) * p;
      value += 0.27 * noise(p);
      p = mat2(1.28, -0.56, 0.56, 1.28) * p;
      value += 0.15 * noise(p);
      return value;
    }

    vec3 grainHash3(vec2 p) {
      return vec3(
        hash12(p + vec2(0.17, 11.93)),
        hash12(p + vec2(13.41, -7.28)),
        hash12(p + vec2(-9.64, 5.72))
      ) - 0.5;
    }

    vec3 grainNoise3(vec2 p) {
      return vec3(
        noise(p + vec2(4.73, -3.16)),
        noise(p + vec2(-8.24, 6.58)),
        noise(p + vec2(12.61, 1.94))
      ) - 0.5;
    }

    vec2 rotate2d(vec2 p, float angle) {
      float s = sin(angle);
      float c = cos(angle);
      return mat2(c, -s, s, c) * p;
    }

    float influence(vec2 p, vec2 center, float falloff) {
      vec2 delta = p - center;
      return exp(-dot(delta, delta) * falloff);
    }

    void main() {
      float aspect = u_resolution.x / max(u_resolution.y, 1.0);
      float portraitBias = clamp((0.92 - aspect) / 0.34, 0.0, 1.0);
      vec2 p = v_uv - 0.5;
      p.x *= aspect;

      p = rotate2d(p, radians(u_rotation));
      p *= u_scale;

      float t = u_time * (0.35 + u_speed * 0.85);

      vec2 drift = vec2(
        softFbm(p * 0.88 + vec2(0.0, t * 0.44)),
        softFbm(p * 0.88 + vec2(4.8, -t * 0.40))
      ) - 0.5;

      p += drift * (0.46 * u_distortion);

      float radius = length(p);
      float twistField = softFbm(p * 0.96 + vec2(t * 0.16, -t * 0.14));
      float twist = (twistField - 0.5) * PI * (u_swirl * 1.15);
      twist *= 1.15 - smoothstep(0.0, 1.35, radius);
      p = rotate2d(p, twist);

      vec2 c0 = u_centers[0] + vec2(0.22 * sin(t * 1.18), 0.18 * cos(t * 0.93));
      vec2 c1 = u_centers[1] + vec2(0.18 * cos(t * 0.78 + 1.7), 0.17 * sin(t * 1.06));
      vec2 c2 = u_centers[2] + vec2(0.12 * sin(t * 0.65 + 2.4), 0.14 * cos(t * 0.74 + 0.4));
      vec2 c3 = u_centers[3] + vec2(0.24 * cos(t * 1.14 + 3.05), 0.16 * sin(t * 0.86 + 1.15));
      vec2 c4 = u_centers[4] + vec2(0.15 * sin(t * 0.91 + 4.1), 0.20 * cos(t * 1.02 + 2.2));
      vec2 c5 = u_centers[5] + vec2(0.20 * cos(t * 1.23 + 0.8), 0.13 * sin(t * 0.82 + 3.6));
      vec2 c6 = u_centers[6] + vec2(0.16 * sin(t * 0.72 + 5.0), 0.18 * cos(t * 1.31 + 1.4));
      vec2 c7 = u_centers[7] + vec2(0.19 * cos(t * 0.68 + 2.9), 0.15 * sin(t * 1.16 + 4.7));

      c0 += vec2(0.16 * portraitBias, 0.02 * portraitBias);
      c1 += vec2(0.08 * portraitBias, 0.04 * portraitBias);
      c2 += vec2(0.30 * portraitBias, -0.02 * portraitBias);
      c3 += vec2(-0.24 * portraitBias, -0.08 * portraitBias);
      c4 += vec2(-0.08 * portraitBias, 0.08 * portraitBias);
      c5 += vec2(0.20 * portraitBias, -0.05 * portraitBias);
      c6 += vec2(-0.18 * portraitBias, 0.02 * portraitBias);
      c7 += vec2(0.10 * portraitBias, -0.10 * portraitBias);

      float falloff = mix(2.25, 3.15, clamp(1.0 - u_distortion * 0.65, 0.0, 1.0));
      float separationPower = mix(1.35, 1.75, clamp(u_edge, 0.0, 1.0));
      float w0 = influence(p, c0, falloff * mix(1.0, 0.88, portraitBias));
      float w1 = influence(p, c1, falloff * mix(1.02, 1.08, portraitBias));
      float w2 = influence(p, c2, falloff * mix(1.18, 1.62, portraitBias));
      float w3 = influence(p, c3, falloff * mix(0.96, 0.84, portraitBias));
      float w4 = influence(p, c4, falloff * mix(1.12, 1.28, portraitBias));
      float w5 = influence(p, c5, falloff * mix(0.92, 1.06, portraitBias));
      float w6 = influence(p, c6, falloff * mix(1.24, 1.44, portraitBias));
      float w7 = influence(p, c7, falloff * mix(1.04, 0.94, portraitBias));

      w0 = pow(w0, separationPower);
      w1 = pow(w1, separationPower);
      w2 = pow(w2, separationPower);
      w3 = pow(w3, separationPower);
      w4 = pow(w4, separationPower);
      w5 = pow(w5, separationPower);
      w6 = pow(w6, separationPower);
      w7 = pow(w7, separationPower);

      float total = w0 + w1 + w2 + w3 + w4 + w5 + w6 + w7 + 0.00001;
      w0 /= total;
      w1 /= total;
      w2 /= total;
      w3 /= total;
      w4 /= total;
      w5 /= total;
      w6 /= total;
      w7 /= total;

      vec3 color = u_colors[0] * w0
        + u_colors[1] * w1
        + u_colors[2] * w2
        + u_colors[3] * w3
        + u_colors[0] * w4
        + u_colors[1] * w5
        + u_colors[2] * w6
        + u_colors[3] * w7;

      vec2 blurOffset = vec2(0.085, -0.065);
      float bw0 = influence(p + blurOffset, c0, falloff * mix(1.0, 0.88, portraitBias));
      float bw1 = influence(p + blurOffset, c1, falloff * mix(1.02, 1.08, portraitBias));
      float bw2 = influence(p + blurOffset, c2, falloff * mix(1.18, 1.62, portraitBias));
      float bw3 = influence(p + blurOffset, c3, falloff * mix(0.96, 0.84, portraitBias));
      float bw4 = influence(p + blurOffset, c4, falloff * mix(1.12, 1.28, portraitBias));
      float bw5 = influence(p + blurOffset, c5, falloff * mix(0.92, 1.06, portraitBias));
      float bw6 = influence(p + blurOffset, c6, falloff * mix(1.24, 1.44, portraitBias));
      float bw7 = influence(p + blurOffset, c7, falloff * mix(1.04, 0.94, portraitBias));

      bw0 = pow(bw0, separationPower);
      bw1 = pow(bw1, separationPower);
      bw2 = pow(bw2, separationPower);
      bw3 = pow(bw3, separationPower);
      bw4 = pow(bw4, separationPower);
      bw5 = pow(bw5, separationPower);
      bw6 = pow(bw6, separationPower);
      bw7 = pow(bw7, separationPower);

      float btotal = bw0 + bw1 + bw2 + bw3 + bw4 + bw5 + bw6 + bw7 + 0.00001;
      bw0 /= btotal;
      bw1 /= btotal;
      bw2 /= btotal;
      bw3 /= btotal;
      bw4 /= btotal;
      bw5 /= btotal;
      bw6 /= btotal;
      bw7 /= btotal;

      vec3 blurredColor = u_colors[0] * bw0
        + u_colors[1] * bw1
        + u_colors[2] * bw2
        + u_colors[3] * bw3
        + u_colors[0] * bw4
        + u_colors[1] * bw5
        + u_colors[2] * bw6
        + u_colors[3] * bw7;

      color = mix(color, blurredColor, 0.16);

      float dominance = max(max(max(w0, w1), max(w2, w3)), max(max(w4, w5), max(w6, w7)));
      float boundary = 1.0 - smoothstep(0.46, 0.96, dominance);
      vec3 paperGrain = grainNoise3(p * 1.9 + vec2(5.2, 2.4));
      color += paperGrain * boundary * u_edge * 0.12;

      vec3 edgeGrain = grainNoise3(p * 2.4 + vec2(-2.2, 6.1));
      color -= edgeGrain * boundary * u_edge * 0.07;

      float glow = smoothstep(1.15, 0.12, radius);
      color = mix(color, vec3(1.0), glow * mix(0.05, 0.022, portraitBias));
      float luminance = dot(color, vec3(0.299, 0.587, 0.114));
      color = mix(vec3(luminance), color, 1.08);

      vec3 colorFilm = grainHash3(gl_FragCoord.xy + vec2(u_time * 48.0, -u_time * 33.0));
      float filmAverage = dot(colorFilm, vec3(0.333333));
      vec3 chromaFilm = colorFilm - vec3(filmAverage);
      vec3 fineColorFilm = grainNoise3(gl_FragCoord.xy * 0.75 + vec2(u_time * 0.9, -u_time * 0.7));
      color += chromaFilm * u_film * 0.24;
      color += colorFilm * u_film * 0.08;
      color += fineColorFilm * u_film * 0.08;

      // Monochrome: collapse the colour and the chroma grain to grey, so the
      // gradient renders in black and white only.
      color = mix(color, vec3(dot(color, vec3(0.299, 0.587, 0.114))), u_mono);

      color = clamp(color, 0.0, 1.0);
      color = pow(color, vec3(0.96));

      gl_FragColor = vec4(color, 1.0);
    }
  `;

  function parseNumber(value, fallback) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function parseColors(value, fallback) {
    const list = String(value || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

    return list.length >= 4 ? list.slice(0, 4) : fallback.slice();
  }

  function parseBoolean(value, fallback) {
    if (value === undefined) {
      return fallback;
    }

    return value === "" || value === "true" || value === "1";
  }

  function toGrey(hex) {
    const rgb = hexToRgb(hex);
    const level = Math.round((rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114) * 255);
    const part = level.toString(16).padStart(2, "0");
    return "#" + part + part + part;
  }

  function hexToRgb(hex) {
    let value = String(hex || "").trim().replace("#", "");
    if (value.length === 3) {
      value = value.split("").map((char) => char + char).join("");
    }

    if (!/^[0-9a-fA-F]{6}$/.test(value)) {
      value = "ffffff";
    }

    return [
      parseInt(value.slice(0, 2), 16) / 255,
      parseInt(value.slice(2, 4), 16) / 255,
      parseInt(value.slice(4, 6), 16) / 255
    ];
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function randomBetween(min, max) {
    return min + Math.random() * (max - min);
  }

  function distance(a, b) {
    const dx = a[0] - b[0];
    const dy = a[1] - b[1];
    return Math.hypot(dx, dy);
  }

  function createRandomCenters() {
    const baseCenters = [
      [-0.95, -0.44],
      [0.88, -0.56],
      [-0.02, 0.02],
      [0.36, 0.82],
      [0.64, -0.02],
      [-0.62, 0.54],
      [0.02, -0.76],
      [-0.86, 0.04]
    ];
    const jitterRanges = [
      [0.42, 0.28],
      [0.34, 0.24],
      [0.40, 0.32],
      [0.34, 0.22],
      [0.28, 0.30],
      [0.30, 0.26],
      [0.34, 0.18],
      [0.24, 0.34]
    ];
    let bestCenters = baseCenters;
    let bestSpacing = -Infinity;

    for (let attempt = 0; attempt < 24; attempt += 1) {
      const centers = baseCenters.map(function (center, index) {
        const jitter = jitterRanges[index];
        return [
          clamp(center[0] + randomBetween(-jitter[0], jitter[0]), -1.1, 1.1),
          clamp(center[1] + randomBetween(-jitter[1], jitter[1]), -0.95, 0.95)
        ];
      });

      let minSpacing = Infinity;
      for (let index = 0; index < centers.length; index += 1) {
        for (let next = index + 1; next < centers.length; next += 1) {
          minSpacing = Math.min(minSpacing, distance(centers[index], centers[next]));
        }
      }

      if (minSpacing > bestSpacing) {
        bestCenters = centers;
        bestSpacing = minSpacing;
      }

      if (minSpacing >= 0.38) {
        return centers;
      }
    }

    return bestCenters;
  }

  function createShader(gl, type, source) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const error = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      throw new Error(error || "Shader compilation failed.");
    }

    return shader;
  }

  function createProgram(gl) {
    const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexSource);
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
    const program = gl.createProgram();

    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const error = gl.getProgramInfoLog(program);
      gl.deleteProgram(program);
      throw new Error(error || "Program linking failed.");
    }

    return program;
  }

  function setFallbackBackground(root, colors, centers, aspect) {
    const portraitBias = clamp((0.92 - aspect) / 0.34, 0, 1);
    const fallbackStops = centers.map(function (center) {
      return {
        x: clamp(((center[0] + 1.1) / 2.2) * 100, 0, 100),
        y: clamp(((center[1] + 0.95) / 1.9) * 100, 0, 100)
      };
    });

    fallbackStops[0].x = clamp(fallbackStops[0].x + 8 * portraitBias, 0, 100);
    fallbackStops[2].x = clamp(fallbackStops[2].x + 16 * portraitBias, 0, 100);
    fallbackStops[3].x = clamp(fallbackStops[3].x - 12 * portraitBias, 0, 100);
    fallbackStops[4].x = clamp(fallbackStops[4].x - 4 * portraitBias, 0, 100);
    fallbackStops[5].x = clamp(fallbackStops[5].x + 10 * portraitBias, 0, 100);
    fallbackStops[6].x = clamp(fallbackStops[6].x - 9 * portraitBias, 0, 100);
    fallbackStops[7].x = clamp(fallbackStops[7].x + 5 * portraitBias, 0, 100);

    const sizes = [34, 32, 28, 30, 26, 27, 24, 25];
    const radialLayers = fallbackStops.map(function (stop, index) {
      return "radial-gradient(circle at "
        + stop.x + "% " + stop.y + "%, "
        + colors[index % colors.length]
        + " 0%, rgba(0, 0, 0, 0) "
        + sizes[index]
        + "%)";
    });

    root.style.background = radialLayers
      .concat("linear-gradient(145deg, " + colors[1] + " 0%, " + colors[0] + " 52%, " + colors[3] + " 100%)")
      .join(",");
  }

  // data-centers: 16 comma-separated numbers (8 x,y blob centres). Lets a live
  // card start from exactly the same layout as its pre-rendered video/poster.
  function parseCenters(value) {
    const list = String(value || "").split(",").map(Number).filter(Number.isFinite);
    if (list.length < 16) return null;
    const centers = [];
    for (let index = 0; index < 16; index += 2) centers.push([list[index], list[index + 1]]);
    return centers;
  }

  function emit(root, name, detail) {
    root.dispatchEvent(new CustomEvent("paper-gradient:" + name, { detail: detail || {} }));
  }

  // Returns a controller ({ update, destroy }) when the live gradient runs,
  // or null when it can't (no WebGL, shader error); "paper-gradient:unsupported"
  // fires in that case so the page can show something else.
  function mountGradient(root) {
    if (!root || root.dataset.paperGradientMounted === "true") {
      return root && root.paperGradient ? root.paperGradient : null;
    }

    const canvas = root.querySelector("canvas") || root.appendChild(document.createElement("canvas"));
    const monochrome = parseBoolean(root.dataset.monochrome, defaults.monochrome);
    const parsedColors = parseColors(root.dataset.colors, defaults.colors);
    const colors = monochrome ? parsedColors.map(toGrey) : parsedColors;
    const centers = parseCenters(root.dataset.centers) || createRandomCenters();
    const initialRect = root.getBoundingClientRect();
    const initialAspect = initialRect.width / Math.max(initialRect.height, 1);
    setFallbackBackground(root, colors, centers, initialAspect);

    const gl = canvas.getContext("webgl", {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      powerPreference: "low-power",
      preserveDrawingBuffer: false
    });

    if (!gl) {
      root.dataset.paperGradientMounted = "fallback";
      emit(root, "unsupported", { reason: "unsupported" });
      return null;
    }

    let program;

    try {
      program = createProgram(gl);
    } catch (error) {
      console.error("[paper-gradient]", error);
      root.dataset.paperGradientMounted = "fallback";
      emit(root, "unsupported", { reason: "unsupported" });
      return null;
    }

    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([
        -1, -1,
         1, -1,
        -1,  1,
         1,  1
      ]),
      gl.STATIC_DRAW
    );

    const positionLocation = gl.getAttribLocation(program, "a_position");
    const resolutionLocation = gl.getUniformLocation(program, "u_resolution");
    const timeLocation = gl.getUniformLocation(program, "u_time");
    const colorsLocation = gl.getUniformLocation(program, "u_colors[0]");
    const centersLocation = gl.getUniformLocation(program, "u_centers[0]");
    const distortionLocation = gl.getUniformLocation(program, "u_distortion");
    const swirlLocation = gl.getUniformLocation(program, "u_swirl");
    const scaleLocation = gl.getUniformLocation(program, "u_scale");
    const rotationLocation = gl.getUniformLocation(program, "u_rotation");
    const speedLocation = gl.getUniformLocation(program, "u_speed");
    const edgeLocation = gl.getUniformLocation(program, "u_edge");
    const filmLocation = gl.getUniformLocation(program, "u_film");
    const monoLocation = gl.getUniformLocation(program, "u_mono");

    let monochromeOn = monochrome;
    let palette = new Float32Array(colors.flatMap(hexToRgb));
    let centerPoints = new Float32Array(centers.flat());
    const settings = {
      distortion: parseNumber(root.dataset.distortion, defaults.distortion),
      swirl: parseNumber(root.dataset.swirl, defaults.swirl),
      scale: parseNumber(root.dataset.scale, defaults.scale),
      rotation: parseNumber(root.dataset.rotation, defaults.rotation),
      speed: parseNumber(root.dataset.speed, defaults.speed),
      edge: parseNumber(root.dataset.edge, defaults.edge),
      film: parseNumber(root.dataset.film, defaults.film),
      maxPixelRatio: parseNumber(root.dataset.maxPixelRatio, defaults.maxPixelRatio),
      maxFps: Math.max(1, parseNumber(root.dataset.maxFps, defaults.maxFps))
    };

    // Quality levels, best first. The gradient drifts slowly, so 30fps looks
    // the same as 60 or 120 while drawing a fraction of the frames. A device
    // that can't keep up (Low Power Mode, a busy CPU or GPU) steps down to
    // fewer pixels and frames, and finally to a still image.
    const levels = [
      { pixelRatio: settings.maxPixelRatio, fps: settings.maxFps },
      { pixelRatio: Math.min(settings.maxPixelRatio, 1), fps: Math.min(settings.maxFps, 24) },
      { pixelRatio: Math.min(settings.maxPixelRatio, 0.6), fps: Math.min(settings.maxFps, 15) },
      { pixelRatio: settings.maxPixelRatio, fps: 0 }
    ];
    const still = levels.length - 1;
    let level = 0;
    let sizeDirty = true;
    let frameId = 0;
    let lastRender = 0;
    let samples = 0;
    let slow = 0;
    let windowStart = 0;
    const startedAt = performance.now();
    let evaluateAfter = startedAt + 3000;
    let drawnOnce = false;
    let onScreen = true;
    let destroyed = false;
    const page = root.closest(".ee-access") || root;

    function resize() {
      sizeDirty = false;
      const rect = root.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, levels[level].pixelRatio);
      const width = Math.max(1, Math.round(rect.width * dpr));
      const height = Math.max(1, Math.round(rect.height * dpr));

      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
      }
    }

    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    function draw(now) {
      if (sizeDirty) resize();

      gl.useProgram(program);
      gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
      gl.uniform1f(timeLocation, (now - startedAt) / 1000);
      gl.uniform3fv(colorsLocation, palette);
      gl.uniform2fv(centersLocation, centerPoints);
      gl.uniform1f(distortionLocation, settings.distortion);
      gl.uniform1f(swirlLocation, settings.swirl);
      gl.uniform1f(scaleLocation, settings.scale);
      gl.uniform1f(rotationLocation, settings.rotation);
      gl.uniform1f(speedLocation, settings.speed);
      gl.uniform1f(edgeLocation, settings.edge);
      gl.uniform1f(filmLocation, settings.film);
      gl.uniform1f(monoLocation, monochromeOn ? 1 : 0);

      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      if (!drawnOnce) {
        drawnOnce = true;
        emit(root, "ready");
      }
    }

    function schedule() {
      if (!frameId && !destroyed && onScreen) frameId = requestAnimationFrame(tick);
    }

    function setLevel(next, reason) {
      if (destroyed) return;
      const target = Math.min(next, still);
      if (target <= level) return;
      level = target;
      if (level === still) emit(root, "still", { reason: reason || "performance" });
      sizeDirty = true;
      samples = 0;
      slow = 0;
      windowStart = 0;
      evaluateAfter = performance.now() + 1500;
      // Pages can drop costlier effects (like live blur) while this is low.
      page.dataset.paperQuality = "low";
      schedule();
    }

    // Count frames that arrive well after they were due, over about a second
    // (or two for very slow devices). Mostly-late frames mean this device is
    // struggling, so step down a level.
    function watch(elapsed, interval, now) {
      if (elapsed > 5000 || now < evaluateAfter) return;
      if (!windowStart) windowStart = now;
      samples += 1;
      if (elapsed > interval * 1.25 + 4) slow += 1;
      if (samples < 5 || (samples < 30 && now - windowStart < 2000)) return;
      if (slow / samples >= 0.4) setLevel(level + 1, "performance");
      samples = 0;
      slow = 0;
      windowStart = 0;
    }

    // A hidden tab pauses frames; don't count the gap when it comes back.
    const resumed = function () {
      lastRender = 0;
      samples = 0;
      slow = 0;
      windowStart = 0;
      evaluateAfter = performance.now() + 1000;
    };

    let resizeObserver = null;
    const markResized = function () {
      sizeDirty = true;
      if (levels[level].fps === 0) schedule();
    };

    let visibilityObserver = null;

    function stop() {
      if (frameId) cancelAnimationFrame(frameId);
      frameId = 0;
      window.removeEventListener("resize", markResized);
      document.removeEventListener("visibilitychange", resumed);
      if (resizeObserver) resizeObserver.disconnect();
      if (visibilityObserver) visibilityObserver.disconnect();
    }

    function tick(now) {
      frameId = 0;
      if (!document.body.contains(root)) {
        stop();
        return;
      }

      const current = levels[level];
      if (current.fps === 0) {
        // Still image: one frame, redrawn only when the size changes.
        draw(now);
        return;
      }

      frameId = requestAnimationFrame(tick);
      const interval = 1000 / current.fps;
      if (lastRender && now - lastRender < interval - 2) return;
      if (lastRender) watch(now - lastRender, interval, now);
      lastRender = now;
      draw(now);
    }

    root.dataset.paperGradientMounted = "true";
    page.dataset.paperQuality = "high";
    window.addEventListener("resize", markResized);
    document.addEventListener("visibilitychange", resumed);
    if (typeof ResizeObserver === "function") {
      resizeObserver = new ResizeObserver(markResized);
      resizeObserver.observe(root);
    }

    // Draw nothing while scrolled off screen (a fixed, full-screen background
    // always counts as on screen).
    if (typeof IntersectionObserver === "function") {
      visibilityObserver = new IntersectionObserver(function (entries) {
        const entry = entries[entries.length - 1];
        onScreen = entry.isIntersecting;
        if (!onScreen && frameId) {
          cancelAnimationFrame(frameId);
          frameId = 0;
        }
        if (onScreen) {
          resumed();
          schedule();
        }
      });
      visibilityObserver.observe(root);
    }

    // Reduced motion, data saver and a low battery (where the browser reports
    // it) start on the still image.
    const reduceMotion = typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)")
      : null;
    if (reduceMotion && reduceMotion.matches) setLevel(still, "reduced-motion");
    if (reduceMotion && typeof reduceMotion.addEventListener === "function") {
      reduceMotion.addEventListener("change", function (event) {
        if (event.matches) setLevel(still, "reduced-motion");
      });
    }
    if (navigator.connection && navigator.connection.saveData) setLevel(still, "save-data");
    if (typeof navigator.getBattery === "function") {
      navigator.getBattery().then(function (battery) {
        const check = function () {
          if (!battery.charging && battery.level <= 0.2) setLevel(still, "battery");
        };
        check();
        battery.addEventListener("levelchange", check);
        battery.addEventListener("chargingchange", check);
      }).catch(function () {});
    }

    const controller = {
      // Swap colours/settings in place (e.g. when a member's tier loads)
      // without creating a new WebGL context.
      update: function (options) {
        if (destroyed || !options) return;
        if (options.monochrome !== undefined) monochromeOn = Boolean(options.monochrome);
        if (options.colors) {
          const list = parseColors(options.colors, defaults.colors);
          palette = new Float32Array((monochromeOn ? list.map(toGrey) : list).flatMap(hexToRgb));
        }
        if (options.centers) {
          const next = parseCenters(options.centers);
          if (next) centerPoints = new Float32Array(next.flat());
        }
        ["distortion", "swirl", "scale", "rotation", "speed", "edge", "film"].forEach(function (key) {
          if (options[key] !== undefined) settings[key] = parseNumber(options[key], settings[key]);
        });
        draw(performance.now());
        schedule();
      },
      destroy: function () {
        if (destroyed) return;
        destroyed = true;
        stop();
        const lose = gl.getExtension("WEBGL_lose_context");
        if (lose) lose.loseContext();
        delete root.paperGradient;
        root.dataset.paperGradientMounted = "destroyed";
      }
    };
    root.paperGradient = controller;

    schedule();
    return controller;
  }

  function mountAll(target) {
    const roots = typeof target === "string"
      ? document.querySelectorAll(target)
      : target instanceof Element
        ? [target]
        : document.querySelectorAll("[data-paper-gradient]");

    roots.forEach(function (root) { mountGradient(root); });
  }

  window.MeshPaperGradient = {
    mount: mountGradient,
    mountAll: mountAll
  };
  document.dispatchEvent(new CustomEvent("paper-gradient:loaded"));

  document.addEventListener("DOMContentLoaded", function () {
    mountAll();
  });

  window.addEventListener("load", function () {
    mountAll();
  });

  document.addEventListener("shopify:section:load", function () {
    mountAll();
  });
})();
